/**
 * DroneControlMap — ArduPilot-inspired operational drone control map.
 *
 * Features
 * ────────────────────────────────────────────────────────────────
 *  • Satellite / street tile toggle (consistent with MissionMap)
 *  • Live drone position marker (pulsing ✈, updates on prop change)
 *  • LEFT-CLICK  → add editable waypoints / area boundary points
 *    - Waypoints are numbered, draggable, persist as route/polygon
 *    - ≥ 3 points → filled polygon overlay (area marking)
 *    - Connecting polyline always visible
 *  • RIGHT-CLICK → drone COMMAND context menu (ArduPilot-style)
 *    Planning mode  : Take Off · Land · Go To Here · Hold · RTL · Drop · Scan · Spray
 *    Control mode   : same commands + Pause Mission · Resume Mission · Start Mission
 *  • Take Control mode (controlMode prop)
 *    - Distinct orange banner "🕹️ שליטה ישירה"
 *    - Commands fire via onCommandIssued callback (lat, lng, cmd)
 *  • Clear-all button
 *  • Distance / area summary strip (bottom)
 *  • readOnly: disables all editing/commands, shows overlay only
 */
import { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import './DroneControlMap.css'

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon   from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl:       markerIcon,
  shadowUrl:     markerShadow,
})

// ─── Types ────────────────────────────────────────────────────────────────────

export type DroneCommand =
  | 'takeoff' | 'land' | 'goto' | 'hold' | 'rtl'
  | 'mission_start' | 'mission_pause' | 'mission_resume'
  | 'drop_delivery' | 'scan' | 'spray'

export interface OverlayPoint {
  lat: number
  lng: number
  label?: string
}

export interface DroneControlMapProps {
  dronePosition?:   { lat: number; lng: number }
  controlMode?:     boolean
  readOnly?:        boolean
  initialPoints?:   OverlayPoint[]
  onPointsChange?:  (pts: OverlayPoint[]) => void
  onCommandIssued?: (cmd: DroneCommand, lat: number, lng: number) => void
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STREET_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
const SAT_URL    = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
const DEFAULT_CENTER: [number, number] = [31.5, 34.9]
const DEFAULT_ZOOM = 9

interface CmdItem { cmd: DroneCommand; label: string; icon: string; color: string; separator?: boolean }

const PLANNING_CMDS: CmdItem[] = [
  { cmd: 'goto',          label: 'לך לכאן',       icon: '🎯', color: '#38bdf8' },
  { cmd: 'takeoff',       label: 'המראה',          icon: '🛫', color: '#4ade80' },
  { cmd: 'land',          label: 'נחיתה',          icon: '🛬', color: '#f87171' },
  { cmd: 'hold',          label: 'עצור במקום',     icon: '⏸', color: '#fbbf24' },
  { cmd: 'rtl',           label: 'חזרה הביתה',     icon: '🏠', color: '#a78bfa', separator: true },
  { cmd: 'drop_delivery', label: 'הורדת משלוח',    icon: '📦', color: '#fb923c' },
  { cmd: 'scan',          label: 'סריקה',           icon: '🔍', color: '#c084fc' },
  { cmd: 'spray',         label: 'ריסוס',           icon: '💧', color: '#34d399' },
]

const CONTROL_CMDS: CmdItem[] = [
  { cmd: 'goto',           label: 'לך לכאן',        icon: '🎯', color: '#38bdf8' },
  { cmd: 'hold',           label: 'עצור במקום',      icon: '⏸', color: '#fbbf24' },
  { cmd: 'takeoff',        label: 'המראה',           icon: '🛫', color: '#4ade80' },
  { cmd: 'land',           label: 'נחיתה',           icon: '🛬', color: '#f87171' },
  { cmd: 'rtl',            label: 'חזרה הביתה (RTL)', icon: '🏠', color: '#a78bfa', separator: true },
  { cmd: 'mission_start',  label: 'התחל משימה',      icon: '▶',  color: '#4ade80' },
  { cmd: 'mission_pause',  label: 'השהה משימה',      icon: '⏸',  color: '#fbbf24' },
  { cmd: 'mission_resume', label: 'המשך משימה',      icon: '⏯',  color: '#38bdf8', separator: true },
  { cmd: 'drop_delivery',  label: 'הורדת משלוח',     icon: '📦', color: '#fb923c' },
  { cmd: 'scan',           label: 'סריקה',            icon: '🔍', color: '#c084fc' },
  { cmd: 'spray',          label: 'ריסוס',            icon: '💧', color: '#34d399' },
]

// ─── Helpers ──────────────────────────────────────────────────────────────────

function haversineM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000
  const φ1 = lat1 * Math.PI / 180, φ2 = lat2 * Math.PI / 180
  const Δφ = (lat2 - lat1) * Math.PI / 180, Δλ = (lng2 - lng1) * Math.PI / 180
  const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

function routeLengthM(pts: OverlayPoint[]): number {
  let d = 0
  for (let i = 1; i < pts.length; i++) d += haversineM(pts[i-1].lat, pts[i-1].lng, pts[i].lat, pts[i].lng)
  return d
}

function polygonAreaM2(pts: OverlayPoint[]): number {
  if (pts.length < 3) return 0
  const R = 6371000
  let area = 0
  for (let i = 0; i < pts.length; i++) {
    const j  = (i + 1) % pts.length
    const xi = pts[i].lng * Math.PI / 180 * R * Math.cos(pts[i].lat * Math.PI / 180)
    const yi = pts[i].lat * Math.PI / 180 * R
    const xj = pts[j].lng * Math.PI / 180 * R * Math.cos(pts[j].lat * Math.PI / 180)
    const yj = pts[j].lat * Math.PI / 180 * R
    area += xi * yj - xj * yi
  }
  return Math.abs(area / 2)
}

// ─── Component ────────────────────────────────────────────────────────────────

interface CtxMenu { x: number; y: number; lat: number; lng: number }

export default function DroneControlMap({
  dronePosition,
  controlMode   = false,
  readOnly      = false,
  initialPoints = [],
  onPointsChange,
  onCommandIssued,
}: DroneControlMapProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapRef          = useRef<L.Map | null>(null)
  const tileLayerRef    = useRef<L.TileLayer | null>(null)
  const droneMarkerRef  = useRef<L.Marker | null>(null)
  const layerGroupRef   = useRef<L.LayerGroup | null>(null)
  const polylineRef     = useRef<L.Polyline | null>(null)
  const polygonRef      = useRef<L.Polygon | null>(null)
  const ptsRef          = useRef<OverlayPoint[]>([])

  const [satellite, setSatellite] = useState(false)
  const [ctxMenu, setCtxMenu]     = useState<CtxMenu | null>(null)
  const [points, setPoints]       = useState<OverlayPoint[]>(initialPoints)
  const [cmdLog, setCmdLog]       = useState<{ cmd: DroneCommand; lat: number; lng: number; ts: number }[]>([])

  ptsRef.current = points

  // ── Tile toggle
  useEffect(() => { tileLayerRef.current?.setUrl(satellite ? SAT_URL : STREET_URL) }, [satellite])

  // ── Notify parent
  useEffect(() => { onPointsChange?.(points) }, [points]) // eslint-disable-line

  // ── Redraw overlay (markers + polyline + polygon) on points change
  useEffect(() => {
    const map = mapRef.current
    const lg  = layerGroupRef.current
    if (!map || !lg) return

    lg.clearLayers()

    // Vertex markers
    points.forEach((pt, idx) => {
      const icon = L.divIcon({
        className: '',
        html: `<div class="dcm-wp-marker">${idx + 1}</div>`,
        iconSize: [26, 26], iconAnchor: [13, 13],
      })
      const m = L.marker([pt.lat, pt.lng], { icon, draggable: !readOnly, zIndexOffset: 200 })
      m.bindTooltip(`WP${idx + 1} · ${pt.lat.toFixed(5)}, ${pt.lng.toFixed(5)}`, {
        permanent: false, direction: 'top', offset: [0, -12],
      })
      if (!readOnly) {
        m.on('dragend', () => {
          const pos = m.getLatLng()
          setPoints(prev => prev.map((p, i) => i === idx ? { ...p, lat: pos.lat, lng: pos.lng } : p))
        })
        m.on('contextmenu', (e: L.LeafletMouseEvent) => {
          e.originalEvent.preventDefault()
          // Remove this point on right-click
          setPoints(prev => prev.filter((_, i) => i !== idx))
        })
      }
      lg.addLayer(m)
    })

    // Polyline
    polylineRef.current?.setLatLngs(points.map(p => [p.lat, p.lng] as L.LatLngTuple))

    // Polygon (≥3 pts)
    if (polygonRef.current) { polygonRef.current.remove(); polygonRef.current = null }
    if (points.length >= 3) {
      polygonRef.current = L.polygon(
        points.map(p => [p.lat, p.lng] as L.LatLngTuple),
        { color: '#38bdf8', weight: 2, dashArray: '6 4', fillColor: '#38bdf8', fillOpacity: 0.12, interactive: false }
      ).addTo(map)
    }
  }, [points, readOnly]) // eslint-disable-line

  // ── Update drone marker
  useEffect(() => {
    if (droneMarkerRef.current && dronePosition) {
      droneMarkerRef.current.setLatLng([dronePosition.lat, dronePosition.lng])
    }
  }, [dronePosition])

  // ── Map init (once)
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return

    const center: [number, number] = dronePosition
      ? [dronePosition.lat, dronePosition.lng]
      : initialPoints.length ? [initialPoints[0].lat, initialPoints[0].lng]
      : DEFAULT_CENTER
    const zoom = dronePosition || initialPoints.length ? 13 : DEFAULT_ZOOM

    const map = L.map(mapContainerRef.current, { center, zoom, zoomControl: true })
    tileLayerRef.current = L.tileLayer(STREET_URL, {
      attribution: '© <a href="https://openstreetmap.org">OpenStreetMap</a>', maxZoom: 19,
    }).addTo(map)

    layerGroupRef.current = L.layerGroup().addTo(map)
    polylineRef.current   = L.polyline([], {
      color: '#38bdf8', weight: 2.5, opacity: 0.85, dashArray: '6 6',
    }).addTo(map)

    // Live drone marker
    if (dronePosition) {
      const di = L.divIcon({
        className: '',
        html: '<div class="dcm-drone-icon">✈</div>',
        iconSize: [36, 36], iconAnchor: [18, 18],
      })
      droneMarkerRef.current = L.marker([dronePosition.lat, dronePosition.lng], { icon: di, zIndexOffset: 1000 })
        .addTo(map).bindPopup('<b>רחפן</b>')
    }

    // Click handlers
    if (!readOnly) {
      map.on('click', (e: L.LeafletMouseEvent) => {
        setCtxMenu(null)
        setPoints(prev => [...prev, { lat: e.latlng.lat, lng: e.latlng.lng }])
      })
      map.on('contextmenu', (e: L.LeafletMouseEvent) => {
        e.originalEvent.preventDefault()
        const cp = map.latLngToContainerPoint(e.latlng)
        setCtxMenu({ x: cp.x, y: cp.y, lat: e.latlng.lat, lng: e.latlng.lng })
      })
    }

    mapRef.current = map

    return () => {
      if (polygonRef.current) { polygonRef.current.remove(); polygonRef.current = null }
      map.remove()
      mapRef.current = tileLayerRef.current = droneMarkerRef.current =
        layerGroupRef.current = polylineRef.current = null
    }
  }, []) // eslint-disable-line

  const issueCommand = (cmd: DroneCommand) => {
    if (!ctxMenu) return
    onCommandIssued?.(cmd, ctxMenu.lat, ctxMenu.lng)
    setCmdLog(prev => [{ cmd, lat: ctxMenu.lat, lng: ctxMenu.lng, ts: Date.now() }, ...prev].slice(0, 5))
    setCtxMenu(null)
  }

  const cmds = controlMode ? CONTROL_CMDS : PLANNING_CMDS
  const dist = routeLengthM(points)
  const area = polygonAreaM2(points)

  return (
    <div
      className="dcm-wrap"
      onClick={() => setCtxMenu(null)}
      onContextMenu={e => e.preventDefault()}
    >
      <div ref={mapContainerRef} className="dcm-canvas" />

      {/* Control mode banner */}
      {controlMode && (
        <div className="dcm-control-banner">🕹️ שליטה ישירה — ArduPilot</div>
      )}

      {/* Satellite toggle */}
      <button
        className="dcm-sat-toggle"
        onClick={e => { e.stopPropagation(); setSatellite(v => !v) }}
        title={satellite ? 'מפת רחובות' : 'תצלום לוויין'}
      >
        {satellite ? '🗺' : '🛰'}
      </button>

      {/* Clear all */}
      {!readOnly && points.length > 0 && (
        <button
          className="dcm-clear-btn"
          onClick={e => { e.stopPropagation(); setPoints([]) }}
        >
          ✕ נקה
        </button>
      )}

      {/* Drone command context menu */}
      {ctxMenu && !readOnly && (
        <div
          className="dcm-ctx-menu"
          style={{ left: ctxMenu.x, top: ctxMenu.y }}
          onClick={e => e.stopPropagation()}
        >
          <div className="dcm-ctx-title">
            {controlMode ? '🕹️ פקודת רחפן' : '🗺️ פעולת מפה'}
          </div>
          {cmds.map((item) => (
            <div key={item.cmd}>
              {item.separator && <div className="dcm-ctx-sep" />}
              <button
                className="dcm-ctx-item"
                style={{ '--cmd-color': item.color } as React.CSSProperties}
                onClick={() => issueCommand(item.cmd)}
              >
                <span className="dcm-ctx-icon">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Command log (control mode) */}
      {controlMode && cmdLog.length > 0 && (
        <div className="dcm-cmd-log">
          {cmdLog.map((entry, i) => {
            const item = CONTROL_CMDS.find(c => c.cmd === entry.cmd)
            return (
              <div key={i} className="dcm-cmd-log-row">
                <span className="dcm-cmd-log-icon">{item?.icon}</span>
                <span className="dcm-cmd-log-label">{item?.label}</span>
                <span className="dcm-cmd-log-coords" dir="ltr">
                  {entry.lat.toFixed(4)}, {entry.lng.toFixed(4)}
                </span>
              </div>
            )
          })}
        </div>
      )}

      {/* Bottom summary strip */}
      {points.length > 0 && (
        <div className="dcm-summary">
          <span>📍 {points.length} נקודות</span>
          {dist > 0 && (
            <span dir="ltr">📏 {dist >= 1000 ? `${(dist / 1000).toFixed(2)} km` : `${Math.round(dist)} m`}</span>
          )}
          {points.length >= 3 && area > 0 && (
            <span dir="ltr">📐 {area >= 10000 ? `${(area / 10000).toFixed(2)} ha` : `${Math.round(area)} m²`}</span>
          )}
          {!readOnly && <span className="dcm-hint">לחץ ימני לפקודת רחפן • גרור נקודה לעריכה</span>}
        </div>
      )}

      {/* Hint overlay */}
      {!readOnly && points.length === 0 && (
        <div className="dcm-hint-overlay">
          לחץ להוסיף נקודות מסלול / אזור • לחץ ימני לפקודת רחפן
        </div>
      )}
    </div>
  )
}
